import type { Currency } from "@core/schema/Currency.js";
import { roundToPlaces } from "@core/utils/roundToPlaces.js";

interface Purchase {
	orderId: string;
	value: number;
	currency: Currency;
}

const reportedPurchasesKey = "metaPixelReportedPurchases";
/** Keeps the stored list from growing without bound; only the newest orders can repeat. */
const maxReportedPurchases = 20;

/**
 * The pixel starts with consent revoked, and the cookie bar can resolve later than the order
 * query behind the confirmation page, so a purchase reported in the meantime waits here instead
 * of being handed to a pixel that would drop it.
 */
let consentGranted = false;
let pendingPurchase: Purchase | undefined;

function readReportedPurchases(): string[] {
	try {
		const stored: unknown = JSON.parse(localStorage.getItem(reportedPurchasesKey) ?? "[]");
		return Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : [];
	} catch {
		// Private mode and blocked site data both throw; without the guard we only risk a repeat,
		// which `eventID` still lets Meta discard.
		return [];
	}
}

function markPurchaseReported(orderId: string, reported: readonly string[]): void {
	try {
		localStorage.setItem(reportedPurchasesKey, JSON.stringify([orderId, ...reported].slice(0, maxReportedPurchases)));
	} catch {
		// See readReportedPurchases.
	}
}

function sendPurchase({ orderId, value, currency }: Purchase): void {
	const { fbq } = window;
	if (!fbq) {
		return;
	}

	const reported = readReportedPurchases();
	if (reported.includes(orderId)) {
		return;
	}

	fbq("track", "Purchase", { value: roundToPlaces(value, 2), currency }, { eventID: `purchase-${orderId}` });
	// Marked only once it is out, so that a missing pixel does not suppress the order for good.
	markPurchaseReported(orderId, reported);
}

/**
 * Whether the base code is in place at all — false when `META_PIXEL` is empty for the environment
 * or a content blocker got in the way. Worth asking before doing work whose only purpose is to
 * decide what to report.
 */
export function isMetaPixelActive(): boolean {
	return window.fbq !== undefined;
}

/** Called by `MetaPixel.svelte` whenever the visitor's targeting consent changes. */
export function setMetaPixelConsent(granted: boolean): void {
	consentGranted = granted;
	if (!granted) {
		return;
	}

	const purchase = pendingPurchase;
	pendingPurchase = undefined;
	if (purchase) {
		sendPurchase(purchase);
	}
}

/**
 * Sends the Meta Pixel `Purchase` event for a completed order. Does nothing when the pixel is
 * not configured for the environment, or when this browser already reported the same order —
 * the confirmation page can be reloaded or reopened after returning from the payment gateway.
 */
export function reportMetaPurchase(orderId: string, value: number, currency: Currency): void {
	if (!window.fbq) {
		return;
	}

	if (!consentGranted) {
		pendingPurchase = { orderId, value, currency };
		return;
	}

	sendPurchase({ orderId, value, currency });
}
